/**
 * Skeleton Components - Loading placeholders
 * 
 * Ces composants affichent des animations de chargement pendant que les données
 * sont récupérées, améliorant l'UX en évitant les "blancs" à l'écran.
 */

import React from 'react';

// Base skeleton with shimmer animation
const shimmerClass = "animate-pulse bg-gradient-to-r from-surface-3 via-surface-2 to-surface-3 bg-[length:200%_100%]";

/**
 * Simple rectangular skeleton
 */
export const Skeleton: React.FC<{
    className?: string;
    width?: string;
    height?: string;
}> = ({ className = '', width = 'w-full', height = 'h-4' }) => (
    <div className={`${shimmerClass} rounded ${width} ${height} ${className}`} />
);

/**
 * Circular skeleton (for avatars)
 */
export const SkeletonCircle: React.FC<{
    size?: 'sm' | 'md' | 'lg' | 'xl';
    className?: string;
}> = ({ size = 'md', className = '' }) => {
    const sizes = {
        sm: 'w-8 h-8',
        md: 'w-10 h-10',
        lg: 'w-12 h-12',
        xl: 'w-16 h-16'
    };
    return <div className={`${shimmerClass} rounded-full ${sizes[size]} ${className}`} />;
};

/**
 * Skeleton for a player card
 */
export const SkeletonPlayerCard: React.FC = () => (
    <div className="bg-surface-2 rounded-xl p-4 border border-white/5">
        <div className="flex items-center gap-3">
            <SkeletonCircle size="lg" />
            <div className="flex-1 space-y-2">
                <Skeleton width="w-32" height="h-4" />
                <Skeleton width="w-20" height="h-3" />
            </div>
            <Skeleton width="w-16" height="h-8" className="rounded-lg" />
        </div>
    </div>
);

/**
 * Skeleton for a match card
 */
export const SkeletonMatchCard: React.FC = () => (
    <div className="bg-surface-2 rounded-lg p-4 border border-white/5">
        <div className="flex justify-between items-center mb-3">
            <Skeleton width="w-24" height="h-3" />
            <Skeleton width="w-16" height="h-5" className="rounded" />
        </div>
        <div className="bg-surface-3/50 rounded-lg p-3 space-y-2">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <SkeletonCircle size="sm" />
                    <Skeleton width="w-20" height="h-3" />
                </div>
                <Skeleton width="w-8" height="h-6" />
            </div>
            <div className="h-px bg-white/5" />
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <SkeletonCircle size="sm" />
                    <Skeleton width="w-20" height="h-3" />
                </div>
                <Skeleton width="w-8" height="h-6" />
            </div>
        </div>
        <Skeleton width="w-full" height="h-9" className="mt-3 rounded-lg" />
    </div>
);

/**
 * Skeleton for a challenge card
 */
export const SkeletonChallengeCard: React.FC = () => (
    <div className="bg-surface-2 rounded-xl p-4 border border-white/5">
        <div className="flex justify-between items-center mb-4">
            <Skeleton width="w-28" height="h-3" />
            <Skeleton width="w-20" height="h-5" className="rounded-full" />
        </div>
        <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-2">
                <SkeletonCircle size="md" />
                <Skeleton width="w-24" height="h-4" />
            </div>
            <Skeleton width="w-8" height="h-6" className="rounded" />
            <div className="flex items-center gap-2">
                <Skeleton width="w-24" height="h-4" />
                <SkeletonCircle size="md" />
            </div>
        </div>
        <div className="flex gap-2 mt-4">
            <Skeleton width="w-1/2" height="h-10" className="rounded-lg" />
            <Skeleton width="w-1/2" height="h-10" className="rounded-lg" />
        </div>
    </div>
);

/**
 * Skeleton for tournament card
 */
export const SkeletonTournamentCard: React.FC = () => (
    <div className="bg-surface-2 rounded-xl p-4 border border-white/5">
        <div className="flex items-center gap-3 mb-3">
            <Skeleton width="w-12" height="h-12" className="rounded-lg" />
            <div className="flex-1 space-y-2">
                <Skeleton width="w-40" height="h-5" />
                <Skeleton width="w-28" height="h-3" />
            </div>
        </div>
        <div className="flex gap-2">
            <Skeleton width="w-20" height="h-6" className="rounded-full" />
            <Skeleton width="w-24" height="h-6" className="rounded-full" />
        </div>
    </div>
);

/**
 * Skeleton for stats card
 */
export const SkeletonStatsCard: React.FC = () => (
    <div className="bg-surface-2 rounded-xl p-4 border border-white/5">
        <Skeleton width="w-20" height="h-3" className="mb-2" />
        <Skeleton width="w-16" height="h-8" className="mb-1" />
        <Skeleton width="w-12" height="h-3" />
    </div>
);

/**
 * Skeleton list - repeats a skeleton component n times
 */
export const SkeletonList: React.FC<{
    count?: number;
    component: React.ReactNode;
    className?: string;
}> = ({ count = 3, component, className = 'space-y-3' }) => (
    <div className={className}>
        {Array.from({ length: count }).map((_, i) => (
            <React.Fragment key={i}>{component}</React.Fragment>
        ))}
    </div>
);

/**
 * Full page skeleton loader
 */
export const SkeletonPage: React.FC<{ title?: string }> = ({ title }) => (
    <div className="p-4 space-y-4 animate-fadeIn">
        {title && (
            <div className="flex items-center gap-3 mb-6">
                <SkeletonCircle size="lg" />
                <Skeleton width="w-48" height="h-6" />
            </div>
        )}
        <SkeletonList count={4} component={<SkeletonPlayerCard />} />
    </div>
);

export default {
    Skeleton,
    SkeletonCircle,
    SkeletonPlayerCard,
    SkeletonMatchCard,
    SkeletonChallengeCard,
    SkeletonTournamentCard,
    SkeletonStatsCard,
    SkeletonList,
    SkeletonPage
};
